import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, EmptyState, JsonView, PageHeader, formatDateTime } from '@/kit/ui'
import { catalogLender, publicLender } from '@/lib/lender'
import { apiMode } from '@/lib/hubApi'
import { getAppCatalog, type AppCatalogView } from '@/lib/catalogueApi'
import { useHubStore } from '@/store/hubStore'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import { useSettingsStore } from '@/store/settingsStore'
import { TEST_APPLICANT } from '@/fixtures/code'
import type { LenderStatusChanged } from '@/types'
import { sampleFor, type Sample } from './hubApiSamples'

// On the API (LH-B4): what the Hub really serves today, and the app's catalog exactly as the app gets it.
const LIVE_ROWS = [
  ['GET /service/v1/app/catalog', 'Web app (app-api, its worker)', 'The mode + every lender in the app: the entry published in a lender’s tab 6 · In the app (contract api/hub-lenders-catalog.v1), ETag / 304, no cache on the way', 'Catalog scope · Authorization: Bearer'],
  ['GET /service/v1/app/lenders/:slug/kfs · /logo', 'Web app (server side)', 'The published KFS PDF · the logo; kept 5 minutes', 'Catalog scope · Authorization: Bearer'],
  ['POST /v1/applications', 'Web app, website', 'Runs the live workflow of endpoint_id; the standard answer; in Workflow runs', 'Applications scope · x-app-key'],
  ['POST /lenders/{slug}{route} · /_hub/health', 'Developers (Postman), a partner', 'One endpoint of the lender’s batch on its container (API catalogue)', 'Lenders · UAT / production scope · x-hub-key'],
  ['event lender.status_changed', 'RabbitMQ, queue app.lender-status', 'slug, status, reason, at, by (the Active switch)', '—'],
  ['/public/* (site, views, the form)', 'The lender landing pages (lenders/)', 'Still served by the old Hub backend (landing pages, later)', 'site key'],
] as const

export function HubApiScreen() {
  return apiMode() ? <HubApiLive /> : <HubApiPreview />
}

function HubApiLive() {
  const lenders = useHubStore((s) => s.lenders)
  const events = useHubStore((s) => s.events)
  const [cat, setCat] = useState<AppCatalogView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [slug, setSlug] = useState('')
  // the endpoint picked in the table (owner: click one, see what it prints); 0 = the app's catalog
  const [picked, setPicked] = useState(0)
  const [whole, setWhole] = useState(false)
  const batches = useCodeStore((s) => s.batches)
  const endpoints = useFlowStore((s) => s.endpoints)
  const hubUrl = useSettingsStore((s) => s.settings.hubUrls.production)
  useEffect(() => { getAppCatalog().then(setCat, (e: Error) => setError(e.message)) }, [lenders])
  const liveBatch = batches.find((b) => b.status === 'live' && b.manifest.endpoints.length)
  const sample = sampleFor(picked, {
    cat, events, applicant: TEST_APPLICANT, hubUrl,
    endpointId: endpoints.find((e) => e.state === 'active')?.id ?? 'moneyview.personal',
    runtime: liveBatch ? { slug: liveBatch.lenderSlug, route: liveBatch.manifest.endpoints[0]!.path, name: liveBatch.lenderSlug } : { slug: 'moneyview', route: '/v2/dedupe', name: 'moneyview' },
  })
  const inApp = cat?.lenders.filter((l) => l.inApp) ?? []
  const shown = slug || inApp[0]?.slug || cat?.lenders[0]?.slug || ''
  const entry = cat?.catalog.lenders.find((l) => l.slug === shown)
  const standing = cat?.lenders.find((l) => l.slug === shown)

  return (
    <>
      <PageHeader title="Hub API & consumers" subtitle="What the Hub serves, to whom, with which key. The keys, scopes and limits are in Settings → Key management." />
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead><tr><th>Endpoint</th><th>Who calls it</th><th>Returns</th><th>Key</th></tr></thead>
            <tbody>{LIVE_ROWS.map((r, i) => (
              <tr key={r[0]} onClick={() => setPicked(i)} className={`cursor-pointer ${i === picked ? 'bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-testid={`hubapi-row-${i}`} aria-selected={i === picked}>
                <td className="mono whitespace-nowrap"><button type="button" className={`text-left ${i === picked ? 'font-semibold text-primary' : 'text-primary underline'}`} onClick={(e) => { e.stopPropagation(); setPicked(i) }}>{r[0]}</button></td>
                <td>{r[1]}</td><td>{r[2]}</td><td className="whitespace-nowrap">{r[3]}</td>
              </tr>
            ))}</tbody>
          </table>
          <p className="p-3 text-2xs text-muted">Click an endpoint to see what it prints. The website does not read the Hub today (its lender cards come from the CMS). <Link className="text-primary underline" to="/settings">Settings → Consumer keys</Link></p>
        </div>
        {picked !== 0 ? <SampleCard sample={sample} /> : (
        <div className="card" data-testid="app-catalog">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="section-title !mb-0">The web app’s catalog, now</h2>
            {cat && <Badge tone={cat.catalog.mode.lenders_live ? 'success' : 'warning'}>{cat.catalog.mode.lenders_live ? `lenders live · ${cat.catalog.mode.live_count}` : cat.catalog.mode.force_prelaunch ? 'pre-launch (forced)' : 'pre-launch (no lender)'}</Badge>}
            {cat && <span className="mono text-2xs text-muted">version {cat.catalog.version}</span>}
            <div className="ml-auto inline-flex overflow-hidden rounded border border-slate-300 dark:border-slate-600" role="radiogroup" aria-label="Show">
              {([['one', 'One lender'], ['whole', 'Whole answer']] as const).map(([v, label]) => (
                <button key={v} type="button" role="radio" aria-checked={(v === 'whole') === whole} onClick={() => setWhole(v === 'whole')} className={`px-2 py-0.5 text-2xs font-medium ${(v === 'whole') === whole ? 'bg-primary text-white' : 'bg-white text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>{label}</button>
              ))}
            </div>
          </div>
          {whole && <SampleParts sample={sample} />}
          {error && <EmptyState title="The catalog did not load" description={error} />}
          {cat && !whole && (
            <>
              <table className="table mb-2">
                <thead><tr><th>Lender</th><th>In the app</th><th>Why not</th></tr></thead>
                <tbody>{cat.lenders.map((l) => (
                  <tr key={l.slug} data-testid={`app-standing-${l.slug}`} className={l.slug === shown ? 'bg-primary/5' : ''}>
                    <td><button type="button" className="text-primary underline" onClick={() => setSlug(l.slug)}>{l.name}</button></td>
                    <td>{l.inApp ? <Badge tone="success">yes</Badge> : <Badge tone="gray">no</Badge>}</td>
                    <td className="text-2xs">{l.why ?? (l.publishedAt ? `published ${formatDateTime(l.publishedAt)} (version ${l.publishedVersion})` : '')}</td>
                  </tr>
                ))}</tbody>
              </table>
              {entry ? <JsonView value={entry} title={`GET /service/v1/app/catalog → lenders[${shown}]`} maxHeight={420} /> : <p className="text-2xs text-muted">{standing ? `${standing.name} is not in the app’s catalog: ${standing.why}.` : 'No lender.'}</p>}
              <p className="mt-2 text-2xs text-muted">The app asks every 5 minutes; a change here (the Active switch, tab 1 “Used by”) reaches it at its next ask. What the entry says is edited and published in each lender’s tab 6 · In the app (hidden until the offer-check journey is built).</p>
            </>
          )}
        </div>
        )}
      </div>
      <EventsCard events={events} title="Events published (lender.status_changed, from the Hub’s outbox)" />
    </>
  )
}

/** What one endpoint prints: the request, then each answer with its HTTP status. */
function SampleCard({ sample }: { sample: Sample }) {
  return (
    <div className="card" data-testid="hubapi-sample">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="section-title !mb-0 mono">{sample.title}</h2>
        {sample.kind === 'live' && <Badge tone="success">live from the Hub</Badge>}
        {sample.kind === 'example' && <Badge tone="gray">example · the contract’s shape</Badge>}
      </div>
      <SampleParts sample={sample} />
    </div>
  )
}

function SampleParts({ sample }: { sample: Sample }) {
  return (
    <>
      <p className="mb-2 text-2xs text-muted">{sample.note}</p>
      <div className="space-y-2">
        {sample.parts.map((p) => (
          <JsonView key={p.label} value={p.value} title={`${p.status !== null ? `${p.status} · ` : ''}${p.label}`} maxHeight={p.label === 'Answer' ? 420 : 220} />
        ))}
      </div>
    </>
  )
}

function EventsCard({ events, title }: { events: LenderStatusChanged[]; title: string }) {
  return (
    <div className="card mt-3">
      <h2 className="section-title">{title}</h2>
      {events.length === 0 ? <p className="text-xs text-muted">None yet. Flip a lender’s switch on the Lenders screen.</p> : (
        <table className="table">
          <thead><tr><th>At</th><th>slug</th><th>status</th><th>reason</th><th>by</th></tr></thead>
          <tbody>{events.map((e, i) => <tr key={i}><td className="mono">{formatDateTime(e.at)}</td><td className="mono">{e.slug}</td><td><Badge tone={e.status === 'active' ? 'success' : 'danger'}>{e.status}</Badge></td><td>{e.reason ?? '—'}</td><td>{e.by}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  )
}

const ROWS = [
  ['GET /public/lenders', 'Website', 'Every active lender: name, slug, logo, colours, short line, products with key facts, links', 'website key'],
  ['GET /service/v1/app/catalog', 'Web app', 'Mode + lenders with rules, logo, KFS, button texts, endpoint_id per product', 'HUB_APP_KEY'],
  ['GET /app/lenders/:slug/kfs · /logo', 'App, website', 'KFS PDF per product · logo light / dark', 'app key · any'],
  ['POST /v1/applications', 'Web app', 'Runs the live workflow of endpoint_id through the Gateway; the standard answer', 'HUB_APP_KEY'],
  ['GET /v1/applications/:ref', 'Web app', 'Later answer for a pending run (the Hub polls the status API)', 'HUB_APP_KEY'],
  ['event lender.status_changed', 'RabbitMQ: Gateway, app, website, tracker', 'slug, status, reason, at, by', '—'],
  ['GET /public/lender', 'Landing page, CMP (later)', 'One lender for a site key', 'site key'],
] as const

/** Hub API & consumers (F1-8) on the fixtures: the endpoint table, the live JSON of a lender from the store, the events. */
function HubApiPreview() {
  const lenders = useHubStore((s) => s.lenders)
  const events = useHubStore((s) => s.events)
  const [slug, setSlug] = useState(lenders[0]?.slug ?? '')
  const [view, setView] = useState<'website' | 'app'>('website')
  const l = lenders.find((x) => x.slug === slug) ?? lenders[0]
  const activeList = lenders.filter((x) => x.status === 'active')

  return (
    <>
      <PageHeader title="Hub API & consumers" subtitle="What the Hub serves, to whom, with which key (the design on the demo data). The web app keeps a 5-minute copy and the last good one for up to 1 hour if the Hub is down." />
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead><tr><th>Endpoint</th><th>Who calls it</th><th>Returns</th><th>Key</th></tr></thead>
            <tbody>{ROWS.map((r) => <tr key={r[0]}><td className="mono whitespace-nowrap">{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td><td className="whitespace-nowrap">{r[3]}</td></tr>)}</tbody>
          </table>
        </div>
        <div className="card">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="section-title !mb-0">Live preview from this portal’s data</h2>
            <select className="input !min-h-7 w-auto py-0.5 text-2xs" value={l?.slug} onChange={(e) => setSlug(e.target.value)} aria-label="Lender">
              {lenders.map((x) => <option key={x.slug} value={x.slug}>{x.name} ({x.status})</option>)}
            </select>
            <div className="inline-flex overflow-hidden rounded border border-slate-300 dark:border-slate-600" role="radiogroup" aria-label="Consumer">
              {(['website', 'app'] as const).map((v) => (
                <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)} className={`px-2 py-0.5 text-2xs font-medium ${view === v ? 'bg-primary text-white' : 'bg-white text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>{v === 'website' ? 'as the website' : 'as the app'}</button>
              ))}
            </div>
          </div>
          {l && l.status !== 'active' && <p className="mb-2 text-2xs text-amber-700 dark:text-amber-300"><Badge tone="warning" className="mr-1">{l.status}</Badge>Not in <code>GET /public/lenders</code> (active lenders only: {activeList.map((x) => x.slug).join(', ') || 'none'}). Shown here for reference.</p>}
          {l && <JsonView value={view === 'website' ? publicLender(l) : catalogLender(l)} title={view === 'website' ? `GET /public/lenders → [ …, ${l.slug} ]` : `GET /service/v1/app/catalog → lenders[${l.slug}]`} maxHeight={520} />}
          <p className="mt-2 text-2xs text-muted">Edit key facts on the lender’s tab 2 and watch this change: that is exactly what the website renders.</p>
        </div>
      </div>
      <EventsCard events={events} title="Events published in this browser session (lender.status_changed)" />
    </>
  )
}
